import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { listForms, deleteForm } from "../../../services/FormsService"
import "react-toastify/dist/ReactToastify.css"
import { handleSuccess, showErrorAlert } from "../../../services/ErrorHandlingService"
import Loader from "../Common/Loader"

const Forms = () => {
  const navigate = useNavigate()
  const [allForms, setAllForms] = useState([])
  const [numRows, setNumRows] = useState(0)
  const [searchQueries, setSearchQueries] = useState({
    form_title: "",
    form_type: "",
    form_status: "",
    form_for: "",
    disease_name: "",
  })
  const [sortField, setSortField] = useState("form_title")
  const [sortOrder, setSortOrder] = useState("asc")
  const [currentPage, setCurrentPage] = useState(0)
  const rowsPerPage = 10
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [activeSearchField, setActiveSearchField] = useState("")
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("")

  useEffect(() => {
    const fetchForms = async () => {
      setLoading(true)
      setError("")
      try {
        if (location.state?.returnTo) {
          const { currentPage, searchQueries, sortField, sortOrder } = location.state.returnTo
          setCurrentPage(currentPage || 0)
          setSearchQueries(searchQueries || {})
          setSortField(sortField || "form_title")
          setSortOrder(sortOrder || "asc")
        }
        await fetchFormsFromServer()
      } catch (err) {
        console.error("Failed to fetch forms:", err)
        setError("Failed to load forms. Please try again.")
      } finally {
        setLoading(false)
      }
    }
    fetchForms()
  }, [])

  const fetchFormsFromServer = async (
    search_by = "",
    search_keyword = "",
    pagination = true,
    pageid = 0,
    sort_by = sortField,
    sort_order = sortOrder
  ) => {
    try {
      const loginDetails = JSON.parse(localStorage.getItem("loginDetails"))
      const admin_id = loginDetails?.id 
      const payload = {
        admin_id,
        search_by,
        search_keyword,
        sort_by,
        sort_order,
        pagination,
        pageid,
      }
      const res = await listForms(payload)
      if (res.data.status === "success") {
        setAllForms(res.data.data || [])
        setNumRows(res.data.num_rows || 0)
      } else {
        setError("Failed to fetch forms")
        showErrorAlert("Failed to fetch forms")
      }
    } catch (err) {
      console.error("Failed to fetch forms:", err)
      setError("Failed to load forms.")
      showErrorAlert("Failed to load forms.")
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    fetchFormsFromServer(
      activeSearchField,
      activeSearchKeyword,
      true,
      page,
      sortField,
      sortOrder
    )
  }

  // Modified handleSearch to clear other fields
  const handleSearch = (e, field) => {
    const keyword = e.target.value

    // Clear other search fields when typing in current field
    const clearedQueries = {
      form_title: "",
      form_type: "",
      form_status: "",
      form_for: "",
      disease_name: "",
    }

    const updatedQueries = { ...clearedQueries, [field]: keyword }
    setSearchQueries(updatedQueries)
    setCurrentPage(0)

    // Track active search
    setActiveSearchField(field)
    setActiveSearchKeyword(keyword)
    fetchFormsFromServer(field, keyword, true, 0, sortField, sortOrder)
  }

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc"
    setSortField(field)
    setSortOrder(order)
    setCurrentPage(0)
    fetchFormsFromServer(activeSearchField, activeSearchKeyword, true, 0, field, order)
  }

  const handleEdit = (form) => {
    navigate(`/forms/edit-form/${form.form_id || form.id}`, {
      state: {
        form,
        returnTo: {
          currentPage,
          searchQueries,
          sortField,
          sortOrder,
        },
      },
    })
  }

  const handleDelete = (form) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this form!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
      customClass: {
        confirmButton: "btn btn-white",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true)
          const loginDetails = JSON.parse(localStorage.getItem("loginDetails"))
          const admin_id = loginDetails?.id || 1
          await deleteForm({ admin_id, form_id: form.form_id || form.id })
          handleSuccess("Form deleted successfully")
          await fetchFormsFromServer(
            activeSearchField,
            activeSearchKeyword,
            true,
            currentPage,
            sortField,
            sortOrder
          )
        } catch (err) {
          const errorMessage = err?.response?.data?.messages?.error || "Error deleting form"
          setError("Failed to delete form")
          showErrorAlert(errorMessage)
          console.error("Delete failed:", err)
        } finally {
          setLoading(false)
        }
      }
    })
  }

  const handleOpenNewTab = (form_id) => {
    const encodedFormId = btoa(form_id)
    window.open(`/forms/patient-form/${encodedFormId}`, "_blank")
  }

  const handlePreview = (formId) => {
    handleOpenNewTab(formId)
  }

  const paginatedData = allForms
  const totalPages = Math.ceil(numRows / rowsPerPage)

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link
            to="/forms/create-form"
            state={{
              returnTo: {
                currentPage,
                searchQueries,
                sortField,
                sortOrder,
              },
            }}
            className="btn btn-primary btn-rounded"
          >
            + Add Form
          </Link>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th
                            onClick={() => handleSort("form_title")}
                            style={{ cursor: "pointer", minWidth: "250px", verticalAlign: "top" }}
                            className="position-relative"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span>Form Title  <span>{sortField === "form_title" && (sortOrder === "asc" ? "↑" : "↓")}</span></span>

                            </div>
                            <input
                              className="form-control form-control-sm"
                              value={searchQueries.form_title}
                              onChange={(e) => handleSearch(e, "form_title")}
                              placeholder="Search Title"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th
                            onClick={() => handleSort("form_type")}
                            style={{ cursor: "pointer", minWidth: "150px", verticalAlign: "top" }}
                            className="position-relative"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span>Type<span>{sortField === "form_type" && (sortOrder === "asc" ? "↑" : "↓")}</span></span>

                            </div>
                            <input
                              className="form-control form-control-sm"
                              value={searchQueries.form_type}
                              onChange={(e) => handleSearch(e, "form_type")}
                              placeholder="Search Type"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th
                            onClick={() => handleSort("disease_name")}
                            style={{ cursor: "pointer", minWidth: "150px", verticalAlign: "top" }}
                            className="position-relative"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span>Disease <span>{sortField === "disease_name" && (sortOrder === "asc" ? "↑" : "↓")}</span></span>
                            </div>
                            <input
                              className="form-control form-control-sm"
                              value={searchQueries.disease_name || ""}
                              onChange={(e) => handleSearch(e, "disease_name")}
                              placeholder="Search Disease"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>

                          <th
                            onClick={() => handleSort("form_for")}
                            style={{ cursor: "pointer", minWidth: "150px", verticalAlign: "top" }}
                            className="position-relative"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span>Form For <span>{sortField === "form_for" && (sortOrder === "asc" ? "↑" : "↓")}</span></span>

                            </div>
                            <input
                              className="form-control form-control-sm"
                              value={searchQueries.form_for}
                              onChange={(e) => handleSearch(e, "form_for")}
                              placeholder="Search For"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th
                            onClick={() => handleSort("form_status")}
                            style={{ cursor: "pointer", minWidth: "150px", verticalAlign: "top" }}
                            className="position-relative"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span>Status<span>{sortField === "form_status" && (sortOrder === "asc" ? "↑" : "↓")}</span></span>

                            </div>
                            <input
                              className="form-control form-control-sm"
                              value={searchQueries.form_status}
                              onChange={(e) => handleSearch(e, "form_status")}
                              placeholder="Search Status"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th style={{ minWidth: "150px", verticalAlign: "top" }}>
                            <div className="mb-2">Actions</div>
                            <div style={{ height: "31px" }}></div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((form, index) => (
                          <tr key={form.form_id || index}>
                            <td className="align-middle">{form.form_title || form.title}</td>
                            <td className="align-middle">{form.Type || form.form_type}</td>
                            <td className="align-middle">
                              {form.form_type?.toLowerCase() === "disease form"
                                ? (form.disease_name || form.disease_id || "-")
                                : "-"}
                            </td>
                            <td className="align-middle">{form.form_for}</td>
                            <td className="align-middle">
                              <span
                                className={`badge px-3 py-1 rounded-pill text-white ${form.form_status === "Published"
                                  ? "bg-success"
                                  : form.form_status === "Draft"
                                    ? "bg-warning"
                                    : "bg-secondary"
                                  }`}
                              >
                                {form.form_status}
                              </span>
                            </td>
                            <td className="align-middle">
                              <div className="d-flex">
                                <span
                                  className="edit me-2"
                                  onClick={() => handleEdit(form)}
                                  style={{ cursor: "pointer" }}
                                  title="Edit Form"
                                >
                                  <i className="fa fa-pencil-alt text-success"></i>
                                </span>
                                <span
                                  className="delete me-2"
                                  onClick={() => handleDelete(form)}
                                  style={{ cursor: "pointer" }}
                                  title="Delete Form"
                                >
                                  <i className="fa fa-trash text-danger"></i>
                                </span>
                                <span
                                  className="preview me-2"
                                  onClick={() => handlePreview(form.form_id || form.id)}
                                  style={{ cursor: "pointer" }}
                                  title="Preview Form"
                                >
                                  <i className="fa fa-eye text-info"></i>
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {paginatedData.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center">
                              No forms found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      Showing {allForms?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                      {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                      <button
                        // onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      {(() => {
                        const pages = []
                        const maxPagesToShow = 5
                        const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow))
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages)
                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => handlePageChange(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>
                          )
                          if (startPage > 1) {
                            pages.push(
                              <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                                ...
                              </span>
                            )
                          }
                        }
                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>
                          )
                        }
                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                                ...
                              </span>
                            )
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => handlePageChange(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>
                          )
                        }
                        return pages
                      })()}
                      <button
                        // onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Forms;